import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { Metric, MetricsCardComponent, StateMessageConfig } from '@groundworks/ngx-groundworks-ui';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../../config.service';
import { BranchInfo } from '../../../sdk/sdk';
import { BranchMetricsService } from '../../../services/branch-metrics.service';

export interface MetricsCard {
  metrics: Metric[];
  state: StateMessageConfig | undefined;
}

@Component({
  selector: 'app-monthly-capacity-metrics',
  imports: [MetricsCardComponent],
  templateUrl: './monthly-capacity-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthlyCapacityMetricsComponent {
  private static readonly SLOT_PRIMARY = 0;
  private static readonly SLOT_REVENUE = 1;
  private static readonly SLOT_SECONDARY = 2;
  private static readonly SLOT_CAPACITY = 3;

  private static readonly INITIAL_STATE: StateMessageConfig = { type: 'initial', message: 'Select a branch to view metrics.' };
  private static readonly LOADING_STATE: StateMessageConfig = { type: 'loading', message: 'Loading metrics...' };
  private static readonly ERROR_STATE: StateMessageConfig = { type: 'error', message: 'Unable to load metrics.' };

  private readonly branchMetricsService = inject(BranchMetricsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private metricsSubscription?: Subscription;

  /**
   * MOD-4787: read once at construction. Every card on this calendar is a revenue/capacity
   * metric, so when the flag is off all four still render — title only, no value, no state
   * message — and no branch-metrics endpoint is ever called.
   */
  private readonly metricsEnabled = inject(ConfigService).enableCalendarMetrics;

  private static readonly PAST_TITLES = ['Actual vs Plan', 'Carry Forward Revenue', 'Rollover Revenue', 'Missed Capacity'];
  private static readonly CURRENT_TITLES = ['Planned Revenue', 'Projected Revenue', 'Plan Percentage', 'Remaining Capacity'];

  /** Stand-in value for a card whose metric is never fetched — reads better than "No data available". */
  private static readonly BLANK_VALUE = '--';

  selectedBranch = input<BranchInfo | null>(null);
  startDate = input<Date | null>(null);
  endDate = input<Date | null>(null);
  paused = input(false);

  cards: MetricsCard[] = [];
  isPastMonth = false;

  private lastLoadedKey: string | null = null;

  private readonly loadKey = computed(() => {
    const branch = this.selectedBranch();
    const start = this.startDate();
    if (branch?.id == null || start == null) return null;
    return `${branch.id}|${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
  });

  constructor() {
    effect(() => {
      const key = this.loadKey();
      if (!this.metricsEnabled) {
        this.renderBlankCards();
        return;
      }
      if (key == null) {
        this.resetToInitial();
        return;
      }
      if (this.paused() || key === this.lastLoadedKey) return;
      this.lastLoadedKey = key;
      this.loadMetrics();
    });
    this.destroyRef.onDestroy(() => this.metricsSubscription?.unsubscribe());
  }

  /** Titles over a `--` placeholder for the gated-off state: the layout is preserved, the values are not fetched. */
  private renderBlankCards(): void {
    const startDate = this.startDate();
    this.isPastMonth = startDate != null && this.isMonthInPast(startDate);
    const titles = this.isPastMonth
      ? MonthlyCapacityMetricsComponent.PAST_TITLES
      : MonthlyCapacityMetricsComponent.CURRENT_TITLES;
    this.cards = titles.map(title => ({
      metrics: [{ title, highlightText: MonthlyCapacityMetricsComponent.BLANK_VALUE }],
      state: undefined,
    }));
    this.cdr.markForCheck();
  }

  private resetToInitial(): void {
    this.metricsSubscription?.unsubscribe();
    this.lastLoadedKey = null;
    this.resetState();
    this.cdr.markForCheck();
  }

  private resetState(): void {
    this.cards = this.buildInitialCards();
    this.isPastMonth = false;
  }

  private buildInitialCards(): MetricsCard[] {
    return [
      { metrics: [], state: MonthlyCapacityMetricsComponent.INITIAL_STATE },
      { metrics: [], state: MonthlyCapacityMetricsComponent.INITIAL_STATE },
      { metrics: [], state: MonthlyCapacityMetricsComponent.INITIAL_STATE },
      { metrics: [], state: MonthlyCapacityMetricsComponent.INITIAL_STATE },
    ];
  }

  private buildLoadingCards(): MetricsCard[] {
    return [
      { metrics: [], state: MonthlyCapacityMetricsComponent.LOADING_STATE },
      { metrics: [], state: MonthlyCapacityMetricsComponent.LOADING_STATE },
      { metrics: [], state: MonthlyCapacityMetricsComponent.LOADING_STATE },
      { metrics: [], state: MonthlyCapacityMetricsComponent.LOADING_STATE },
    ];
  }

  private loadMetrics(): void {
    this.metricsSubscription?.unsubscribe();

    const branchId = this.selectedBranch()!.id!;
    const startDate = this.startDate()!;
    this.isPastMonth = this.isMonthInPast(startDate);

    this.cards = this.buildLoadingCards();
    this.cdr.markForCheck();

    if (this.isPastMonth) {
      this.loadPastMonthMetrics(branchId, startDate);
    } else {
      this.loadCurrentFutureMonthMetrics(branchId, startDate);
    }
  }

  private loadPastMonthMetrics(branchId: string, startDate: Date): void {
    const subscription = new Subscription();

    subscription.add(
      this.branchMetricsService.getActualVsPlan(branchId, startDate).subscribe({
        next: (metric) => {
          if (metric?.actualRevenue != null) {
            this.cards[MonthlyCapacityMetricsComponent.SLOT_PRIMARY] = {
              metrics: [{
                title: 'Actual vs Plan',
                highlightText: this.formatCurrency(metric.actualRevenue),
                changeText: `${metric.changeIndicator > 0 ? '+' : ''}${metric.changeIndicator}%`,
                changeType: metric.changeIndicator < 0 ? 'negative' : 'positive',
              }],
              state: undefined,
            };
          } else {
            this.cards[MonthlyCapacityMetricsComponent.SLOT_PRIMARY] = {
              metrics: [{ title: 'Actual vs Plan' }],
              state: undefined,
            };
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_PRIMARY] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    subscription.add(
      this.branchMetricsService.getCarryForwardRevenue(branchId, startDate).subscribe({
        next: (revenue) => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_REVENUE] = {
            metrics: [{ title: 'Carry Forward Revenue', highlightText: this.formatCurrency(revenue.revenue) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_REVENUE] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    subscription.add(
      this.branchMetricsService.getRolloverRevenue(branchId, startDate).subscribe({
        next: (rollover) => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_SECONDARY] = {
            metrics: [{ title: 'Rollover Revenue', highlightText: this.formatCurrency(rollover.revenue) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_SECONDARY] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    subscription.add(
      this.branchMetricsService.getMonthlyCapacity(branchId, startDate).subscribe({
        next: (capacity) => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_CAPACITY] = {
            metrics: [{ title: 'Missed Capacity', highlightText: this.formatCurrency(capacity.capacity) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_CAPACITY] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    this.metricsSubscription = subscription;
  }

  private loadCurrentFutureMonthMetrics(branchId: string, startDate: Date): void {
    const subscription = new Subscription();

    subscription.add(
      this.branchMetricsService.getPlannedRevenue(branchId, startDate).subscribe({
        next: (metric) => {
          if (metric?.revenue != null) {
            this.cards[MonthlyCapacityMetricsComponent.SLOT_PRIMARY] = {
              metrics: [{ title: 'Planned Revenue', highlightText: this.formatCurrency(metric.revenue) }],
              state: undefined,
            };
          } else {
            this.cards[MonthlyCapacityMetricsComponent.SLOT_PRIMARY] = {
              metrics: [{ title: 'Planned Revenue' }],
              state: undefined,
            };
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_PRIMARY] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    subscription.add(
      this.branchMetricsService.getMonthlyRevenue(branchId, startDate).subscribe({
        next: (metric) => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_REVENUE] = {
            metrics: [{ title: 'Projected Revenue', highlightText: this.formatCurrency(metric.revenue) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_REVENUE] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    subscription.add(
      this.branchMetricsService.getPlanPercentage(branchId, startDate).subscribe({
        next: (metric) => {
          if (metric?.percentage != null) {
            this.cards[MonthlyCapacityMetricsComponent.SLOT_SECONDARY] = {
              metrics: [{ title: 'Plan Percentage', highlightText: `${metric.percentage}%` }],
              state: undefined,
            };
          } else {
            this.cards[MonthlyCapacityMetricsComponent.SLOT_SECONDARY] = {
              metrics: [{ title: 'Plan Percentage' }],
              state: undefined,
            };
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_SECONDARY] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    subscription.add(
      this.branchMetricsService.getMonthlyCapacity(branchId, startDate).subscribe({
        next: (capacity) => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_CAPACITY] = {
            metrics: [{ title: 'Remaining Capacity', highlightText: this.formatCurrency(capacity.capacity) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[MonthlyCapacityMetricsComponent.SLOT_CAPACITY] = { metrics: [], state: MonthlyCapacityMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    this.metricsSubscription = subscription;
  }

  private isMonthInPast(calendarStartDate: Date): boolean {
    const inMonth = new Date(calendarStartDate.getFullYear(), calendarStartDate.getMonth(), calendarStartDate.getDate() + 7);
    const lastDayOfMonth = new Date(inMonth.getFullYear(), inMonth.getMonth() + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return lastDayOfMonth < today;
  }

  private formatCurrency(amount: number): string {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
}
